import { useState } from "react";
import { Link } from "react-router-dom";
import { Bot, Users, Search, TrendingUp, BookOpen, ChevronDown, Menu, X } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import "@/pages/home.css";

const BOOK_URL = "https://calendar.app.google/MFyPpbRo7HG92Q1YA";
const LINKEDIN_URL = "https://www.linkedin.com/in/craig-maloney-marketing/";
const ASSESSMENT_URL = "/assessment";
const HEADSHOT_URL =
  "https://customer-assets.emergentagent.com/job_craig-revenue/artifacts/5xwn181n_Craig_Pro_Headshot.webp";

const GauntletIcon = ({ size = 40 }) => (
  <svg
    viewBox="0 0 40 40"
    width={size}
    height={size}
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <defs>
      <linearGradient id="cm-steel" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#e5e7eb" />
        <stop offset="0.55" stopColor="#94a3b8" />
        <stop offset="1" stopColor="#475569" />
      </linearGradient>
    </defs>
    <g stroke="#334155" strokeWidth="1.2" strokeLinejoin="round" fill="url(#cm-steel)">
      <path d="M8 30 L8 36 L26 36 L26 30 Z" />
      <path d="M9 18 L9 30 L26 30 L26 20 L23 18 Z" />
      <path d="M26 20 L31 22 L31 27 L26 27 Z" />
      <rect x="10" y="8" width="4" height="12" rx="1.2" />
      <rect x="14" y="6" width="4" height="14" rx="1.2" />
      <rect x="18" y="6" width="4" height="14" rx="1.2" />
      <rect x="22" y="8" width="4" height="12" rx="1.2" />
    </g>
    <g stroke="#334155" strokeWidth="0.8" fill="none">
      <line x1="10" y1="12" x2="14" y2="12" />
      <line x1="14" y1="10" x2="18" y2="10" />
      <line x1="18" y1="10" x2="22" y2="10" />
      <line x1="22" y1="12" x2="26" y2="12" />
      <line x1="9" y1="24" x2="26" y2="24" />
    </g>
  </svg>
);

const PRODUCTS = [
  {
    name: "Business Model Gauntlet",
    desc: "Market Assessment Tool",
    url: "https://gauntlet.craigmaloney.com",
    icon: <GauntletIcon />,
  },
  {
    name: "MyAnswerWiki",
    desc: "Strategic Training for Agent Teams",
    url: "https://myanswerwiki.com/",
    icon: <Bot size={26} />,
  },
  {
    name: "Executive Command Center",
    desc: "Agentic Management Team",
    url: "#",
    icon: <Users size={26} />,
  },
  {
    name: "AEO Builder",
    desc: "LLMs.txt creation + Citation Database citations",
    url: "#",
    icon: <Search size={26} />,
  },
  {
    name: "StrategicROI",
    desc: "Agentic Marketing Team",
    url: "#",
    icon: <TrendingUp size={26} />,
  },
  {
    name: "The Sales Ledger",
    desc: "Owned CRM & Computer",
    url: "#",
    icon: <BookOpen size={26} />,
  },
];

const DropdownItem = ({ p, i, onClick }) => (
  <a
    href={p.url}
    target="_blank"
    rel="noopener noreferrer"
    className="dd-item"
    data-testid={`dropdown-item-${i}`}
    onClick={onClick}
  >
    <span className="dd-num">{i + 1}</span>
    <div className="dd-body">
      <div className="dd-name">{p.name}</div>
      <div className="dd-desc">{p.desc}</div>
    </div>
  </a>
);

export default function Landing() {
  useSeo({
    title: "Craig Maloney — Accelerating Growth for Business Leaders",
    description:
      "AI Growth Consultant. Six connected AI products, one closed-loop system — take the stress test, then build only the pieces your business needs to win.",
    canonical: "https://craigmaloney.com/",
    og: {
      title: "Craig Maloney — Accelerating Growth for Business Leaders",
      description:
        "Six connected AI products, one closed-loop system — take the stress test, then build only the pieces your business needs to win.",
      image: "https://craigmaloney.com/og-home.png",
      type: "website",
      url: "https://craigmaloney.com/",
    },
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: "Craig Maloney, AI Growth Consultant",
      url: "https://craigmaloney.com/",
      description:
        "Six connected AI products, one closed-loop system — take the stress test, then build only the pieces your business needs to win.",
      founder: {
        "@type": "Person",
        name: "Craig Maloney",
        jobTitle: "AI Growth Consultant",
      },
      areaServed: "US",
    },
  });

  const [mobileOpen, setMobileOpen] = useState(false);
  const [imgOk, setImgOk] = useState(true);
  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="home-page" data-testid="landing">
      {/* ============================ NAV ============================ */}
      <nav className="nav">
        <div className="wrap nav-row">
          <Link to="/" className="brand" data-testid="nav-brand">Craig Maloney</Link>

          <div className="nav-desktop">
            <div className="dd-wrap" data-testid="nav-products-wrap">
              <button className="nav-link dd-trigger" type="button" data-testid="nav-products-btn">
                Products <ChevronDown size={16} />
              </button>
              <div className="dd-menu" role="menu">
                {PRODUCTS.map((p, i) => (
                  <DropdownItem key={p.name} p={p} i={i} />
                ))}
              </div>
            </div>
            <a href="#about" className="nav-link" data-testid="nav-about-link">About</a>
            <a
              href={BOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-orange btn-sm"
              data-testid="nav-book-btn"
            >
              Book a Call
            </a>
          </div>

          <button
            className="hamburger"
            type="button"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="nav-hamburger"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {mobileOpen && (
          <div className="nav-mobile" data-testid="nav-mobile">
            <div className="wrap">
              <div className="mm-heading">Products</div>
              <div className="mm-items">
                {PRODUCTS.map((p, i) => (
                  <DropdownItem key={p.name} p={p} i={i} onClick={closeMobile} />
                ))}
              </div>
              <a href="#about" className="mm-link" onClick={closeMobile}>About</a>
              <a
                href={BOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-orange mm-book"
                onClick={closeMobile}
              >
                Book a Call
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* ============================ HERO ============================ */}
      <header className="hero">
        <div className="wrap hero-row">
          <div className="hero-photo" data-testid="hero-photo">
            {imgOk ? (
              <img src={HEADSHOT_URL} alt="Craig Maloney" onError={() => setImgOk(false)} />
            ) : (
              <div className="hero-photo-fallback" aria-hidden="true" />
            )}
          </div>
          <div className="hero-text">
            <h1 className="h1" data-testid="hero-h1">
              Accelerating Growth for Business Leaders
            </h1>
            <p className="hero-sub">
              Six connected AI products, one closed-loop system — take the stress test,
              then build only the pieces your business needs to win.
            </p>
            <div className="hero-ctas">
              <Link
                to={ASSESSMENT_URL}
                className="btn-orange btn-lg"
                data-testid="hero-assessment-btn"
              >
                Assess Your Business&apos;s AI Readiness <span className="arr">&rarr;</span>
              </Link>
              <a
                href={BOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline btn-lg"
                data-testid="hero-book-btn"
              >
                Book a Call
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* ==================== SYSTEM INTRO ==================== */}
      <section className="intro" id="about">
        <div className="wrap intro-inner">
          <h2 className="h2" data-testid="intro-h2">
            One growth system. Six connected products.
          </h2>
          <p className="intro-copy">
            Each product builds on the one before it — start by confirming your idea is
            ready to go to market. Convert your intellectual property in your mind to
            an LLMs.txt file that trains your digital duplicate, board of directors,
            and full-service marketing team. Store it all on your owned computer,
            track your success, scale, and grow revenue.
          </p>
        </div>
      </section>

      {/* ==================== PRODUCT TIMELINE ==================== */}
      <section className="timeline-section">
        <div className="wrap">
          <ol className="timeline" data-testid="product-timeline">
            {PRODUCTS.map((p, i) => (
              <li className="tl-item" key={p.name} data-testid={`tl-item-${i}`}>
                <span className="tl-dot" aria-hidden="true">{i + 1}</span>
                <div className="tl-card">
                  <div className="tl-icon">{p.icon}</div>
                  <div className="tl-text">
                    <strong>{p.name}</strong>
                    <span className="tl-sep">|</span>
                    <span className="tl-desc">{p.desc}</span>
                  </div>
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-orange tl-btn"
                    data-testid={`tl-cta-${i}`}
                  >
                    {p.name} <span className="arr" aria-hidden="true">&#8599;</span>
                  </a>
                </div>
              </li>
            ))}
          </ol>

          <p className="timeline-caption" data-testid="timeline-caption">
            Start with an idea. Go to market. Scale success with automation. Be found on
            LLMs. Build your digital duplicate and executive team to move forward with
            confidence at speed. Own your data.
          </p>
        </div>
      </section>

      {/* ============================ FOOTER ============================ */}
      <footer className="footer">
        <div className="wrap footer-grid">
          <div className="footer-brand" data-testid="footer-brand">
            <div className="fb-name">Craig Maloney</div>
            <div className="fb-role">AI GROWTH CONSULTANT</div>
            <div className="fb-tag" data-testid="footer-tagline">
              Accelerating Growth for Business Leaders
            </div>
          </div>

          <div className="footer-col">
            <div className="fc-title">Tools for Success</div>
            <a href={PRODUCTS[0].url} target="_blank" rel="noopener noreferrer" className="fc-link">{PRODUCTS[0].name}</a>
            <a href={PRODUCTS[1].url} target="_blank" rel="noopener noreferrer" className="fc-link">{PRODUCTS[1].name}</a>
            <a href={PRODUCTS[2].url} target="_blank" rel="noopener noreferrer" className="fc-link">{PRODUCTS[2].name}</a>
          </div>

          <div className="footer-col">
            <div className="fc-title fc-title-spacer" aria-hidden="true">&nbsp;</div>
            <a href={PRODUCTS[3].url} target="_blank" rel="noopener noreferrer" className="fc-link">{PRODUCTS[3].name}</a>
            <a href={PRODUCTS[4].url} target="_blank" rel="noopener noreferrer" className="fc-link">{PRODUCTS[4].name}</a>
            <a href={PRODUCTS[5].url} target="_blank" rel="noopener noreferrer" className="fc-link">{PRODUCTS[5].name}</a>
          </div>

          <div className="footer-col">
            <div className="fc-title">Connect</div>
            <a href={BOOK_URL} target="_blank" rel="noopener noreferrer" className="fc-link">Book a Call</a>
            <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className="fc-link">LinkedIn</a>
          </div>
        </div>
        <div className="footer-bottom">
          <div className="wrap">© 2026 Craig Maloney. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}
